<script setup lang="ts">
import { computed, ref } from "vue";
import { storeToRefs } from "pinia";
import { useClipboard } from "@vueuse/core";
import { Check, Copy, Share2 } from "lucide-vue-next";
import { useResultsV2Store } from "@stores/survey/resultsV2/resultsV2";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Tooltip } from "@/components/ui/tooltip";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const store = useResultsV2Store();
const { currentView, isDefaultView } = storeToRefs(store);
const { copy, copied } = useClipboard();

const open = ref(false);
const saving = ref(false);

const shared = computed<boolean>(() => currentView.value?.shared ?? false);
const shareUrl = computed(() => `${window.location.origin}/shared/dashboards/${currentView.value?.token}`);

async function toggleShare(enabled: boolean) {
  const viewToken = currentView.value?.token;
  if (!viewToken) return;
  saving.value = true;
  try {
    await (enabled ? store.shareView(viewToken) : store.unshareView(viewToken));
  } finally {
    saving.value = false;
  }
}
</script>

<template>
  <Tooltip
    :disabled="!isDefaultView"
    content="The default dashboard cannot be shared. Duplicate it to share a copy."
    content-class="text-wrap">
    <span>
      <Button variant="ghost" :disabled="isDefaultView" @click="open = true">
        <Share2 class="mr-1.5 h-3.5 w-3.5" />
        Share
      </Button>
    </span>
  </Tooltip>

  <Dialog v-model:open="open">
    <DialogContent>
      <DialogHeader>
        <DialogTitle>Share dashboard</DialogTitle>
        <DialogDescription>
          People opening the link see this dashboard only, read-only, without an account.
        </DialogDescription>
      </DialogHeader>

      <div class="flex items-center justify-between gap-4 rounded-lg border p-3">
        <Label for="share-dashboard" class="cursor-pointer text-sm font-medium">Anyone with the link can view</Label>
        <Switch id="share-dashboard" :model-value="shared" :disabled="saving" @update:model-value="toggleShare" />
      </div>

      <div v-if="shared" class="flex items-center gap-2">
        <Input :model-value="shareUrl" readonly class="flex-1" @focus="($event.target as HTMLInputElement).select()" />
        <Button variant="outline" @click="copy(shareUrl)">
          <Check v-if="copied" class="mr-1.5 h-3.5 w-3.5" />
          <Copy v-else class="mr-1.5 h-3.5 w-3.5" />
          {{ copied ? "Copied" : "Copy" }}
        </Button>
      </div>
    </DialogContent>
  </Dialog>
</template>
